import React, { type FC, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';

import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { createAgentTrustReceiptPanel } from './createAgentTrustReceiptPanel.tsx';
import type { AgentTrustReceipt } from './agentTrustReceiptModel.ts';
import { resolveAgentTrustReceipt } from './resolveAgentTrustReceipt.ts';

const i18n = defineMessages({
	chipLabel: {
		id: 'agent-trust-receipt.byline-chip.label',
		defaultMessage: 'Agent receipt',
		description: 'Byline button label that opens the Agent Trust Receipt panel.',
	},
});

type AgentTrustReceiptBylineChipProps = {
	contentId: string;
};

type AgentTrustReceiptBylineChipButtonProps = {
	receipt: AgentTrustReceipt;
};

const AgentTrustReceiptBylineChipButton: FC<AgentTrustReceiptBylineChipButtonProps> = ({
	receipt,
}) => {
	const { formatMessage } = useIntl();
	const { showObjectSidebar } = useObjectSidebarActions();

	const openReceiptPanel = useCallback(() => {
		showObjectSidebar(createAgentTrustReceiptPanel({ receipt }), 'push');
	}, [receipt, showObjectSidebar]);

	return (
		<Button
			appearance="subtle"
			spacing="compact"
			iconBefore={AiAgentIcon}
			testId="agent-trust-receipt-byline-chip"
			onClick={openReceiptPanel}
		>
			{formatMessage(i18n.chipLabel)}
		</Button>
	);
};

const AgentTrustReceiptBylineChipInner: FC<AgentTrustReceiptBylineChipProps> = ({ contentId }) => {
	const receipt = useMemo(() => resolveAgentTrustReceipt(contentId), [contentId]);

	if (!receipt) {
		return null;
	}

	return <AgentTrustReceiptBylineChipButton receipt={receipt} />;
};

export const AgentTrustReceiptBylineChip: React.FC<AgentTrustReceiptBylineChipProps> =
	AgentTrustReceiptBylineChipInner;
